import { CommonModule } from '@angular/common';
import { AfterViewInit, ApplicationRef, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { GenericInputComponent } from '@components/generic-input/generic-input.component';

import { PublicQueryService } from '../../services/public-query.service';
import { FaviconService } from '../../services/favicon.service';
import { FileService } from '../../services/file.service';

import { Expedition } from '../../interfaces/expedition.interface';

import formatDate from '../../utils/formatDate';

import * as L from 'leaflet';
import 'leaflet-polylinedecorator';
import { OpenModalConfirmService } from '@services/open-modal-confirm.service';

@Component({
  selector: 'app-safetrace-public-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    GenericInputComponent
  ],
  templateUrl: './safetrace-public-panel.component.html',
  styleUrl: './safetrace-public-panel.component.scss'
})
export class SafetracePublicPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  titleService = inject(Title);
  faviconService = inject(FaviconService);
  fileService = inject(FileService);
  publicQueryService = inject(PublicQueryService);
  formBuilder = inject(FormBuilder);
  activatedRoute = inject(ActivatedRoute);
  appRef = inject(ApplicationRef);
  openModalConfirmService = inject(OpenModalConfirmService);
  public formatDate = formatDate;

  inputCode: string = '';
  isModalOpen: boolean = false;
  currentModal: number | null = null;
  loading: boolean = false;
  showTraceability: boolean = false;
  isMobile: boolean = false;
  logoUrl: string = '';
  safetraceData?: Expedition[];
  form!: FormGroup;
  placeholder: string = '';
  private map: any;
  private resizeObserver: any;
  private mutationObserver: any;
  identifier: string = '';
  trackingCode: string = '';
  searchType: 'identifier' | 'trackingCode' = 'identifier';
  customerDocument: string = '';
  product: string = '';
  ean: string = '';
  providerDocument: string = '';
  origins: any[] = [];
  expandedOrigins: boolean[] = [];
  expandedLastOrigins: boolean[] = [];
  placesCentroids: L.Marker[] = [];
  lastOriginName: string = '';
  currentIndex: number = 0;
  dateNow: Date = new Date();
  searchWithoutParams: boolean = false;
  productsList: { name: string, value: string }[] = [];
  moreThenOneResult: boolean = false;
  incompleteData: boolean = false;
  institutionalSite: string = 'https://safetrace.com.br/';
  productImages: string[] = ['../../../assets/images/safetrace/rastreability-default.png'];

  @ViewChild('mapArea', { static: false }) mapArea!: ElementRef;
  @ViewChild('resultCard', { static: false }) resultCard!: ElementRef;

  constructor() {
    this.form = this.formBuilder.group({
      code: ['', Validators.required],
      product: [''],
      invoiceNumber: [''],
      productionDate: ['']
    });
  }

  ngOnInit(): void {
    this.titleService.setTitle('Safe Trace - Painel Público');
    this.faviconService.changeFavicon('assets/images/safetrace/favicon.ico');

    this.activatedRoute.queryParamMap.subscribe(params => {
      this.identifier = params.get('id') ?? '';
      this.trackingCode = params.get('code') ?? '';
      this.product = params.get('product') ?? '';
      this.customerDocument = params.get('customer') ?? '';
      this.ean = params.get('EAN') ?? '';
      this.providerDocument = params.get('DOC') ?? '';
      if (this.identifier) {
        this.searchType = 'identifier';
        this.form.get('code')?.setValue(this.identifier.trim());
        this.getProductInfo();
      } else if (this.trackingCode) {
        this.searchType = 'trackingCode';
        this.form.get('code')?.setValue(this.trackingCode.trim());
        this.getProductInfo();
      } else if (this.ean && this.providerDocument) {
        this.form.get('code')?.clearValidators();
        this.form.get('code')?.updateValueAndValidity();
        this.publicQueryService.findProductByEanAndProviderDoc(this.ean, this.providerDocument).subscribe({
          next: (res: any) => {
            if (res.product) {
              this.product = res.product.hash ?? '';
            }
            if (res.enterprise.features.some((item: any) => item.name === 'logo')) {
              this.getLogo(res.enterprise.features.find((item: any) => item.name === 'logo').value)
            }
            if (this.product && !this.moreThenOneResult) {
              this.openModalConfirmService.openModalConfirm({
                text: 'Bem vindo ao painel público da SafeTrace',
                subText: 'Digite o código do produto e sua data de produção para consultar os dados de rastreabilidade',
                type: 'success',
                hideCancelButton: true,
                hideConfirmIcon: true,
                confirmText: 'Prosseguir'
              });
            } else {
              this.openModalConfirmService.openModalConfirm({
                text: 'Este produto não possui dados suficientes para montagem da cadeia de rastreabilidade.',
                type: 'error',
                hideCancelButton: true,
                hideConfirmIcon: true,
                confirmText: 'Fechar'
              });
            }
          },
          error: () => {
            this.openModalConfirmService.openModalConfirm({
              text: 'Erro ao buscar dados do produto, por favor, tente novamente mais tarde.',
              type: 'error',
              hideCancelButton: true,
              hideConfirmIcon: true,
              confirmText: 'Fechar'
            });
          }
        });
      } else {
        this.searchWithoutParams = true;
        this.searchType = 'trackingCode';
        this.form.get('product')?.disable();
      }
    });

    this.getDevice();
    this.disableZoomForMobile();
  }

  ngAfterViewInit(): void {
    this.initMap();
    window.addEventListener('resize', this.handleWindowResize);

    try {
      if (this.mapArea && (window as any).ResizeObserver) {
        this.resizeObserver = new (window as any).ResizeObserver(() => {
          this.refreshMap(20);
        });
        this.resizeObserver.observe(this.mapArea.nativeElement);
      } else if (this.mapArea) {
        this.mutationObserver = new MutationObserver(() => {
          this.refreshMap(80);
        });
        this.mutationObserver.observe(this.mapArea.nativeElement, { attributes: true, attributeFilter: ['style', 'class'] });
      }
    } catch (e) { }
  }

  getProductInfo(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const codeValue = this.form.get('code')?.value ? this.form.get('code')?.value : '';
      const payload = {
        identifier: this.identifier ? this.identifier : (this.searchType === 'identifier' ? codeValue : ''),
        trackingCode: this.trackingCode ? this.trackingCode : (this.searchType === 'trackingCode' ? codeValue : ''),
        customerDocument: this.customerDocument ? this.customerDocument : '',
        providerDocument: this.providerDocument ? this.providerDocument : '',
        productHash: this.product ? this.product : this.form.get('product')?.value ? this.form.get('product')?.value : '',
        invoiceNumber: this.form.get('invoiceNumber')?.value.slice(0,-1) ?? '',
        productionDate: this.form.get('productionDate')?.value ?? ''
      }
      this.publicQueryService.findAllByIdentificador(payload).subscribe({
        next: (res: Expedition[]) => {
          this.safetraceData = res;
          if (this.safetraceData.length > 1) {
            this.moreThenOneResult = true;
            this.form.get('product')?.enable();
            this.form.get('product')?.setValidators([Validators.required]);
            this.form.get('product')?.updateValueAndValidity();

            if (!this.form.get('product')?.value) {
              this.safetraceData.map((item: any) => {
                this.productsList.push({ name: `${item.invoiceItem.invoice.number.slice(0,-1)} - ${item.invoiceItem.product.name} da ${item.customerName ?? '-'}`, value: item.invoiceItem.invoice.number });
              });

              this.openModalConfirmService.openModalConfirm({
                text: 'Selecione o produto e sua respectiva empresa',
                type: 'warning',
                hideCancelButton: true,
                hideConfirmIcon: false,
                confirmText: 'Entendido'
              });
              this.form.markAllAsTouched();
            }
          } else {
            this.form.get('product')?.removeValidators;
            this.form.get('product')?.updateValueAndValidity();
            this.form.get('product')?.disable();

            this.lastOriginName = this.safetraceData[0].customerName ? this.safetraceData[0].customerName : 'Nome não registrado';

            if (this.safetraceData[0].providerLogo) {
              this.getLogo(this.safetraceData[0].providerLogo)
            }

            if (res[0] && res[0].origins![0]) {
              this.safetraceData![0].origins![0].customer!.features.forEach((feature: any) => {
                if (feature.type === 'institutionalSite') {
                  this.institutionalSite = feature.value;
                }
              });
            }

            this.addMarkersAndFlow();
            this.filterOrigins();
            this.filterProductImages();

            try {
              setTimeout(() => {
                if (this.resultCard && this.resultCard.nativeElement) {
                  this.resultCard.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  this.refreshMap(300);
                } else {
                  const section = document.querySelector('.traceability');
                  if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  this.refreshMap(300);
                }
              }, 50);
            } catch (e) {
              console.warn('Erro ao rolar até o card de resultado', e);
            }
          }
        },
        error: () => {
          this.openModalConfirmService.openModalConfirm({
            text: 'Erro ao buscar dados do produto. Por favor, verifique se os dados estão corretos.',
            type: 'error',
            hideCancelButton: true,
            hideConfirmIcon: true,
            confirmText: 'Fechar'
          });
        }
      });
    } else {
      this.openModalConfirmService.openModalConfirm({
        text: 'Preencha todos os dados necessários.',
        type: 'warning',
        hideCancelButton: true,
        hideConfirmIcon: true,
        confirmText: 'Fechar'
      });
    }
  }

  productSelected(invoiceNumber: any): void {
    if (invoiceNumber) {
      const selectedProduct = this.safetraceData?.filter((item: any) => item.invoiceItem.invoice.number == invoiceNumber);
      this.form.get('product')?.setValue(selectedProduct![0].invoiceItem!.product.hash);
      this.safetraceData = this.safetraceData?.filter((item: any) => (item.invoiceItem.product.hash == this.form.get('product')?.value && item.invoiceItem.invoice.number == invoiceNumber));
      this.lastOriginName = this.safetraceData![0].customerName ? this.safetraceData![0].customerName : 'Nome não registrado';
      this.addMarkersAndFlow();
      this.filterOrigins();
      this.filterProductImages();

      try {
        setTimeout(() => {
          if (this.resultCard && this.resultCard.nativeElement) {
            this.resultCard.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            this.refreshMap(300);
          } else {
            const section = document.querySelector('.traceability');
            if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            this.refreshMap(300);
          }
        }, 50);
      } catch (e) {
        console.warn('Erro ao rolar até o card de resultado', e);
      }
    }
  }

  get codePlaceholder(): string {
    return this.searchType === 'trackingCode'
      ? 'Insira o código de rastreio para rastreá-lo'
      : 'Insira o código do produto para rastreá-lo';
  }

  idChange(event: any): void {
    if (!this.form.get('code')?.value) {
      this.moreThenOneResult = false;
      this.form.get('product')?.removeValidators;
      this.form.get('product')?.updateValueAndValidity();
      this.form.get('product')?.setValue('');
      this.form.get('product')?.disable();
    }
  }

  getLogo(logo: string): void {
    this.fileService.downloadPublicFile(logo).subscribe((blob: Blob) => {
      this.logoUrl = URL.createObjectURL(blob);
    });
  }

  disableZoomForMobile() {
    document.addEventListener('dblclick', function (event) {
      event.preventDefault();
    });

    document.addEventListener('touchstart', function (event: TouchEvent) {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    }, { passive: false });

    document.addEventListener('touchmove', function (event: TouchEvent) {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    }, { passive: false });
  }

  getDevice() {
    this.isMobile = window.innerWidth <= 600 ? true : false;
  }

  toggleExpandOrigin(index: number, type: 'lastOrigin' | 'origin'): void {
    if (type === 'origin') {
      this.expandedOrigins[index] = !this.expandedOrigins[index];
    }
    if (type === 'lastOrigin') {
      this.expandedLastOrigins[index] = !this.expandedLastOrigins[index];
    }
  }

  filterOrigins(): void {
    this.safetraceData!.forEach((invoiceItem: any) => {
      if (!this.origins.some((item: any) => item.productName == invoiceItem.invoiceItem.product.name)) {
        const itemOrigins: any[] = [];
        itemOrigins.push({ customer: invoiceItem.customerName || 'Nome não registrado', provider: invoiceItem.providerName || 'Nome não registrado', date: invoiceItem.movementDate })
        if (invoiceItem.origins.length > 0) {
          invoiceItem.origins.forEach((item: any) => {
            itemOrigins.push({ customer: item.customer.corporateName, provider: item.provider.corporateName, date: item.completionDate });
          });
        }
        this.origins.push({ productName: invoiceItem.invoiceItem.product.name, origins: itemOrigins });

      } else {
        const index: number = this.origins.findIndex((element: any) => element.productName == invoiceItem.invoiceItem.product.name);
        const itemOrigins: any[] = [];
        itemOrigins.push({ customer: invoiceItem.customerName || 'Nome não registrado', provider: invoiceItem.providerName || 'Nome não registrado', date: invoiceItem.movementDate });
        if (invoiceItem.origins.length > 0) {
          invoiceItem.origins.forEach((item: any) => {
            itemOrigins.push({ customer: item.customer.corporateName, provider: item.provider.corporateName, date: item.completionDate });
          });
        }

        itemOrigins.forEach((item: any) => {
          const exists = this.origins[index].origins.some((element: any) => element.customer === item.customer && element.provider === item.provider);
          if (!exists) {
            this.origins[index].origins.push({ customer: item.customer, provider: item.provider, date: item.date });
          }
          else {
            this.origins[index].origins.forEach((origin: any, i: number) => {
              if (this.parseDate(item.date) > this.parseDate(origin.date)) {
                this.origins[index].origins[i] = item;
              }
            })
          }
        });
      }
    });
  }

  async filterProductImages() {
    const imagesBase64: string[] = [];

    this.safetraceData![0].invoiceItem!.product!.features.forEach((feature: any) => {
      if (feature.type === 'productImage') {
        this.productImages = [];
        imagesBase64.push(feature.value);
      }
    });

    for (const image of imagesBase64) {
      const blob = await this.fileService.downloadPublicFile(image).toPromise();
      if (blob) {
        this.productImages.push(URL.createObjectURL(blob));
      }
    }
  }

  getLastMovement(productName: string, info: 'customer' | 'provider' | 'date'): string {
    const product = this.origins.find((item: any) => item.productName == productName);
    let lastOrigin: any = '';
    product.origins.forEach((element: any) => {
      if (lastOrigin === '') {
        lastOrigin = element;
      } else {
        if (this.parseDate(element.date) > this.parseDate(lastOrigin.date)) {
          lastOrigin = element;
        }
      }
    });
    switch (info) {
      case 'customer':
        return lastOrigin.customer;
      case 'provider':
        return lastOrigin.provider;
      case 'date':
        return lastOrigin.date;
    }
  }

  parseDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  openModal(modalId: number) {
    this.isModalOpen = true;
    this.currentModal = modalId;
  }

  closeModal() {
    this.isModalOpen = false;
    this.currentModal = null;
  }

  addMarkersAndFlow(): void {
    if (!this.map) {
      console.error('Mapa não inicializado!');
      return;
    }

    this.placesCentroids.forEach(m => m.remove());
    this.placesCentroids = [];

    const flowCoords: any[] = [];

    // 1) Adiciona marcadores e preenche flowCoords
    if (this.safetraceData![0] && this.safetraceData![0].origins!.length > 0) {
      this.safetraceData![0].origins?.forEach((origin: any, index: number) => {
        const provName = origin.provider.corporateName;
        origin.provider.places.forEach((place: any) => {
          const lat = parseFloat(place.latitude);
          const lng = parseFloat(place.longitude);
          try {
            const marker = L.marker([lat, lng], { icon: this.checkOriginType('origin') })
              .addTo(this.map).bindPopup(`<b>ORIGEM:</b> ${provName} - ${place.nickname}`);
            this.placesCentroids.push(marker);
            flowCoords.push([[lat, lng]]);
          } catch {
            console.log(`Erro ao registrar pin de: ${origin.provider.corporateName} para ${origin.customer.corporateName}. Algum deles está sem dados de centróide dos seus locais.`);
          }
        });

        const custName = origin.customer.corporateName;
        origin.customer.places.forEach((place: any) => {
          const lat = parseFloat(place.latitude);
          const lng = parseFloat(place.longitude);
          try {
            const marker = L.marker([lat, lng], { icon: this.checkOriginType('origin') })
              .addTo(this.map).bindPopup(`<b>ORIGEM:</b> ${custName} - ${place.nickname}`);
            this.placesCentroids.push(marker);
            flowCoords[index].push([lat, lng]);
          } catch {
            console.log(`Erro ao registrar pin de: ${origin.customer.corporateName} para ${origin.provider.corporateName}. Algum deles está sem dados de centróide dos seus locais.`);
          }
        });
      });
    }

    if (this.safetraceData![0].customerCentroid!.latitude && this.safetraceData![0].customerCentroid!.longitude && this.safetraceData![0].providerCentroid!.latitude && this.safetraceData![0].providerCentroid!.longitude) {
      const customerName = this.safetraceData![0].customerName;
      const custLat = parseFloat(this.safetraceData![0].customerCentroid!.latitude);
      const custLng = parseFloat(this.safetraceData![0].customerCentroid!.longitude);
      const provLat = parseFloat(this.safetraceData![0].providerCentroid!.latitude);
      const provLng = parseFloat(this.safetraceData![0].providerCentroid!.longitude);

      try {
        const customerMarker = L.marker([custLat, custLng], { icon: this.checkOriginType('lastOrigin') }).
          addTo(this.map).bindPopup(`<b>ÚLTIMO DESTINO:</b> ${customerName}`);
        this.placesCentroids.push(customerMarker);
        flowCoords.push([[provLat, provLng], [custLat, custLng]]);
      } catch {
        console.log(`Erro ao registrar pin de: ${customerName}`);
      }
    };

    if (flowCoords.length > 0) {
      flowCoords.forEach((pair: any) => {
        const polyline = L.polyline(pair, {
          color: '#ffffff',
          weight: 3,
          opacity: 1,
          dashArray: '5,10'  // opcional: linha tracejada
        }).addTo(this.map);

        // Adiciona setas com o decorator
        try {
          const decorator = (L as any).polylineDecorator(polyline, {
            patterns: [
              {
                offset: '10%',    // começa 10% ao longo da linha
                repeat: '10%',    // repete a cada 10%
                symbol: (L as any).Symbol.arrowHead({
                  pixelSize: 8,
                  polygon: false,
                  pathOptions: { stroke: true, color: 'black', weight: 2 }
                })
              }
            ]
          }).addTo(this.map);
        } catch {
          console.log('Erro ao renderizar setas nas linhas.')
        }
      });
      this.incompleteData = false;
      // Desenha a polyline ligando na ordem
      const firstCentroid = flowCoords[0];
      if (firstCentroid && firstCentroid.length > 0) {
        const [lat, lng] = firstCentroid[0] as [number, number];
        this.map.setView([lat, lng], 2);
        this.refreshMap(80);
      }
    } else {
      this.incompleteData = true;
      this.openModalConfirmService.openModalConfirm({
        text: 'Este produto não possui dados suficientes para montagem da cadeia de rastreabilidade no mapa.',
        type: 'warning',
        hideCancelButton: true,
        hideConfirmIcon: true,
        confirmText: 'Fechar'
      });
    }
  }

  refreshMap(delay: number = 50): void {
    try {
      setTimeout(() => {
        try {
          if (this.map && typeof this.map.invalidateSize === 'function') {
            requestAnimationFrame(() => {
              try { this.map.invalidateSize(false); } catch (e) { }
              requestAnimationFrame(() => {
                try { this.map.invalidateSize(false); } catch (e) { }
                try {
                  this.map.eachLayer((layer: any) => {
                    if (layer && typeof layer.redraw === 'function') {
                      layer.redraw();
                    }
                  });
                } catch (e) { }
              });
            });
          }
        } catch (e) { }
      }, delay);
    } catch (e) { }
  }

  private handleWindowResize = () => {
    this.refreshMap(20);
  }

  ngOnDestroy(): void {
    try {
      window.removeEventListener('resize', this.handleWindowResize);
    } catch (e) {
    }

    try {
      if (this.resizeObserver && typeof this.resizeObserver.disconnect === 'function') {
        this.resizeObserver.disconnect();
      }
    } catch (e) { }

    try {
      if (this.mutationObserver && typeof this.mutationObserver.disconnect === 'function') {
        this.mutationObserver.disconnect();
      }
    } catch (e) { }
  }

  checkOriginType(place: string): any {
    switch (place) {
      case ('origin'):
        return this.createPin('Origin');
      case ('lastOrigin'):
        return this.createPin('Distributor');
    }
  }

  createPin(type: string): L.Icon {
    return L.icon({
      iconUrl: `../../../assets/images/cabruca/${type}-Location.svg`,
      iconSize: [45, 61],
      popupAnchor: [0, -50]
    });
  }

  private initMap(): void {
    if (this.map) {
      try { this.refreshMap(60); } catch (e) { }
      return;
    }

    this.map = L.map(this.mapArea.nativeElement, {
      attributionControl: false,
      minZoom: 2,
      maxBounds: [
        [-180, -180],
        [180, 180]
      ],
      maxBoundsViscosity: 1.0,
      scrollWheelZoom: false
    }).setView([-6.9714, -58.5014], 2);

    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'SafeTrace',
    }).addTo(this.map);

    L.control.attribution({
      position: 'topright'
    }).addTo(this.map);

    L.control.scale({ position: 'topleft' }).addTo(this.map);

    const editableLayers = new L.FeatureGroup();
    this.map.addLayer(editableLayers);
  }

  nextImage() {
    if (this.productImages.length > 0) {
      this.currentIndex = (this.currentIndex + 1) % this.productImages.length;
    }
  }

  prevImage() {
    if (this.productImages.length > 0) {
      this.currentIndex = (this.currentIndex - 1 + this.productImages.length) % this.productImages.length;
    }
  }
}
